import {Injectable, OnDestroy} from '@angular/core';
import {
  ClusterMarkerService,
  FitSizePlanEnum,
  MarkerIconTypeEnum,
  Pin,
  PlanService,
  TileLoadStatus,
  TileViewerReadyEvent
} from '@mydocma/tile-viewer';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, firstValueFrom, Subscription, timer} from 'rxjs';
import {MmPlansManagerService} from '../../services/mm-plans-manager.service';
import {PlanService as TileViewerPlanService} from '@mydocma/tile-viewer/lib/plan/plan.service';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {TranslateService} from '@ngx-translate/core';
import {MmProjectPermissionsService} from '../../../../mm-project/mm-project-permissions-service';
import {MmFaultsViewService} from '../../../mm-faults-view/services/mm-faults-view.service';
import {MmPlanEditService} from './mm-plan-edit.service';
import {MmPlanHoverService} from './mm-plan-hover.service';
import {MmPlanPinSelectionService} from './mm-plan-pin-selection.service';
import {debounceTime, filter, first, switchMap, tap, throttleTime} from 'rxjs/operators';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {FaultPin, Plan} from '@docma-mm-core/plan/plan';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {MmPinPopupService} from '../../mm-plan-popup/mm-pin-popup.service';
import {MmFaultsSplitService} from '../../../mm-faults-view/services/mm-faults-split.service';
import {IMmConfig} from '@docma-mm-core/project/project.interface';
import {ConfigProvider} from '@mydocma/common';
import {MmFaultsLoaderService} from '../../../mm-faults-loader.service';
import {AttributeService} from '@docma-mm-core/attribute/attribute.service';
import {AttributeGroup} from '@docma-mm-core/attribute-group/attribute-group';
import {PinMarkerOptions} from '@mydocma/tile-viewer/lib/pin/pin-marker.options';
import {FloatingActionButtonService} from '@mydocma/component-library/src/lib/floating-action-button';
import {AppContainerService} from '@mydocma/app-container';
import {PlanEditModeEnum} from './plan-edit-mode.enum';
import {BpPinLocationTargetEnum} from '../../../../bp-common/models/bp-pin';
import {ProjectSettingsService} from '@docma-mm-core/project-settings/project-settings.service';
import {MmProjectService} from '../../../../mm-project/mm-project.service';
import {Project} from '@docma-mm-core/project/project';
import {MmPlanPinService} from './mm-plan-pin.service';
import {TaskStatusEnum} from '@mydocma/task';
import {Logger} from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class MmPlanViewService implements OnDestroy {

  public isFaultTileVisibleSubject = new BehaviorSubject<boolean>(null);

  public latestAttribute: number;
  private subscriptions: Subscription[] = [];
  private planSub: Subscription;
  public tileViewerEvent: TileViewerReadyEvent;
  private tilePlanService: TileViewerPlanService;
  private permissinEditPin$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditPins]);
  private editPinPermission: boolean;
  private loadedPlanSubject = new BehaviorSubject<Plan>(null);
  loadedPlan$ = this.loadedPlanSubject.asObservable();
  get loadedPlan(): Plan { return this.loadedPlanSubject.value; }
  private isFirstLoad: boolean;
  private project: Project;

  constructor(private readonly plansManagerService: MmPlansManagerService,
              private readonly planPinService: MmPlanPinService,
              private readonly planPinSelectionService: MmPlanPinSelectionService,
              private readonly selectionFaultService: SelectionService<ViewFault>,
              private readonly planHoverService: MmPlanHoverService,
              private readonly notificationService: NotificationService,
              private readonly planEditService: MmPlanEditService,
              private readonly faultsViewService: MmFaultsViewService,
              private readonly translateService: TranslateService,
              private readonly permissionService: MmProjectPermissionsService,
              private readonly faultsSplitService: MmFaultsSplitService,
              private readonly pinPopService: MmPinPopupService,
              private floatingActionButtonService: FloatingActionButtonService,
              private configService: ConfigProvider<IMmConfig>,
              public readonly loaderService: MmFaultsLoaderService,
              private attributeService: AttributeService,
              private projectSettingsService: ProjectSettingsService,
              private projectService: MmProjectService,
              public appContainerService: AppContainerService,
              private readonly logger: Logger) {

    this.planPinService.currentPlanPinsUpdated$
      .pipe(untilDestroyed(this))
      .subscribe(async plan => {
        if (!this.tileViewerEvent || !this.tileViewerEvent.pinService) {
          // tile viewer not ready yet - skip. When planViewReady runs it will set pins for the selected plan.
          return;
        }
        if (plan && this.plansManagerService.selectedPlan && plan?.data?.Id === this.plansManagerService.selectedPlan?.data?.Id) {
          const pins = await Promise.all(plan.pins.map(x => this.convertToPin(x)));
          this.tileViewerEvent.pinService.setPins(pins);
        }

      });

    this.permissinEditPin$.pipe(untilDestroyed(this)).subscribe(p => this.editPinPermission = p);
    this.planEditService.attachTo(this);

    this.faultsSplitService.rightViewVisible$
      .pipe(untilDestroyed(this))
      .subscribe(isVisible => {
        if (!isVisible) {
          this.planPinSelectionService.unregisterPlanPinService();
        } else {
          // only register if the tile viewer is ready
          if (this.tileViewerEvent && this.tileViewerEvent.pinService && this.tileViewerEvent.markerService) {
            this.planPinSelectionService.registerPlanPinService(this.tileViewerEvent.pinService, this.tileViewerEvent.markerService);
          } else {
            // tile viewer not ready yet; registration will occur in planViewReady when the viewer becomes available
          }
        }
      });
  }

  ngOnDestroy(): void {
  }

  // mainly used when we click close button on tile in mobile view
  public async deselectPins() {
    await this.faultsViewService.setCurrentFault(null);
    const selectedPins = this.tileViewerEvent.pinService.selectedPins;
    this.tileViewerEvent.pinService.deselectPins(selectedPins);
  }

  async planViewReady(tileViewerEvent: TileViewerReadyEvent) {
    // console.debug('### plans set ### 01.planViewReady');

    this.planSub?.unsubscribe();
    this.planSub = new Subscription();
    this.tileViewerEvent = tileViewerEvent;
    this.project = await firstValueFrom(this.projectService.currentProject);

    this.tilePlanService = tileViewerEvent.mapService as TileViewerPlanService;
    this.planPinSelectionService.registerPlanPinService(this.tileViewerEvent.pinService, this.tileViewerEvent.markerService);
    this.planHoverService.registerHoverService(this.tileViewerEvent.markerService);
    this.planPinService.registerPlanPinService(this.tileViewerEvent.pinService);


    this.planEditService.registerEditService(this.tileViewerEvent.pinEditService,
      this.tileViewerEvent.pinService,
      this.tileViewerEvent.mapService, this.tileViewerEvent.selectionAreaService);
    this.pinPopService.attachService(this.tileViewerEvent.pinPopupService);

    this.planSub.add(this.plansManagerService.selectedPlan$
      .pipe(
        tap(p => this.resetSubscriptions()),
        tap(p => this.loadedPlanSubject.next(null)),
        filter(plan => plan != null && plan.metaData != null),
        untilDestroyed(this))
      .subscribe(
        async (selectedPlan: Plan) => {
          // console.debug(selectedPlan, '### plans set ### 01 - handleSelectedPlan.selectedPlan - ' + selectedPlan.data.FileName);
          this.handleSelectedPlan(selectedPlan);
        }));

    combineLatest([this.faultsViewService.currentFault, this.tileViewerEvent.pinService.selectedPins$.pipe(filter(Boolean)), this.tileViewerEvent.pinService.pins$.pipe(filter(Boolean))])
      .pipe(untilDestroyed(this), debounceTime(100))
      .subscribe(([currentFault, selectedPins, pins]) => {
        if (this.appContainerService.isMobileView) {

          if (currentFault && !currentFault?.id) {
            setTimeout(() => {
              const p = pins.find(pin => (currentFault?.id != null && pin.data?.id != null && pin.data?.id === currentFault?.id) || pin?.id === currentFault?.uid);
              if (p && this.tileViewerEvent.pinService.selectedPins.findIndex(x => x.id === p.id) === -1) {
                this.tileViewerEvent.pinService.deselectPins(this.tileViewerEvent.pinService.selectedPins.map(m => ({id: m.id})));
                this.tileViewerEvent.pinService.selectPin({id: p.id}, true);
                console.debug('### selectPin: ' + p.id);
              } else if (pins.length === 0 && this.tileViewerEvent.pinService.selectedPins?.length > 0) {
                this.tileViewerEvent.pinService.deselectPins(this.tileViewerEvent.pinService.selectedPins.map(m => ({id: m.id})));
              }
              this.setFaultTileVisiblity(currentFault, pins);
            }, 250);
          } else {
            this.setFaultTileVisiblity(currentFault, pins);
          }
        }
      });
  }

  private resetSubscriptions(): void {
    this.subscriptions?.forEach(s => {
      // console.log('### plans set ### 01 - resetSubscriptions !!');
      s.unsubscribe();
    });
    this.subscriptions = [];
  }

  // private async checkIfTextPinEnabled() {
  //   const project = await firstValueFrom(this.projectService.currentProject);
  //   const projectSettings = await firstValueFrom(this.projectSettingsService.getProjectSettings(project.Id));
  //   return projectSettings.find(value => {
  //     return value.Key === 'IsPlanPinsEnabled' && value.Setting === 'True';
  //   })
  // }
  private createTilePlan(selectedPlan: Plan) {
    return {
      height: selectedPlan.metaData.ImageHeight,
      width: selectedPlan.metaData.ImageWidth,
      maxZoom: selectedPlan.metaData.MaxZoomLevel,
      tileUrl: selectedPlan.metaData.TilesUrl,
      name: selectedPlan.loadState?.data?.FileName || 'file_snapshot',
      isLocalMode: selectedPlan.metaData.IsLocalMode,
      maxNativeZoom: selectedPlan.metaData.MaxNativeZoom,
      updatedAt: selectedPlan.data?.BpPlan?.updatedAt ?? (selectedPlan.data?.ModifiedOn ? new Date(selectedPlan.data.ModifiedOn) : undefined)
    };
  }

  private async handleFaultPins(selectedPlan: Plan) {
    await this.mapSelectedFaultsOnPlan();
  }


  private async handleSelectedPlan(selectedPlan: Plan): Promise<void> {
    if (this.tileViewerEvent.pinService.pins.length) {
      this.tileViewerEvent.pinService.reset();
    }

    this.tilePlanService.loadPlan(null);

    if (!this.hasImageDimensions(selectedPlan)) {
      await this.mapSelectedFaultsOnPlan();
      this.handleFaultPins(selectedPlan);

      if (selectedPlan.data?.BpPlan?.activeVersion?.task?.status == TaskStatusEnum.started || selectedPlan.data?.BpPlan?.activeVersion?.task?.status == TaskStatusEnum.init) {
        this.plansManagerService.isPlanRendering = true;
      }
      if (selectedPlan.data?.BpPlan?.activeVersion?.task?.status == TaskStatusEnum.failed || selectedPlan.data?.BpPlan?.activeVersion?.task?.status == TaskStatusEnum.canceled) {
        this.plansManagerService.isPlanCorrupt = true;
      }
      this.plansManagerService.setPlanLoading(false);
      return;
    }
    try {

      this.plansManagerService.setPlanLoading(true);
      const tilePlan = this.createTilePlan(selectedPlan);
      this.tilePlanService.loadPlan(tilePlan);


      this.subscriptions.push(this.faultsSplitService.rightViewVisible$
        .pipe(
          filter(Boolean),
          first(),
          untilDestroyed(this),
          switchMap(p => timer(300))
        ).subscribe(() => {
          this.onRightViewVisibilityChange();
        }));

      this.subscriptions.push(selectedPlan.pins$.pipe(
        untilDestroyed(this),
        // throttleTime(500),
      ).subscribe(async pins => {
        this.onSelectedPlanPinsChange(pins, selectedPlan);
      }));
      (this.tileViewerEvent.markerService as ClusterMarkerService).clustering = true;
      this.loadedPlanSubject.next(selectedPlan);
    } catch (e) {
      this.logger.error(e, '', 'MmPlanViewService');
      this.notificationService.error(this.translateService.instant('MM.plan.error.loading'));
    } finally {
      this.plansManagerService.setPlanLoading(false);
    }
  }

  private async onRightViewVisibilityChange() {
    this.tilePlanService.fitPlanToContainerBounds(FitSizePlanEnum.fitSize);
    await this.planEditService.centerEditPin();
  }

  private async onSelectedPlanPinsChange(pins: FaultPin[], plan: Plan) {
    const selectedPlan = await firstValueFrom(this.plansManagerService.selectedPlan$);

    if (this.tileViewerEvent.pinService.pins.length) {
      this.tileViewerEvent.pinService.reset();
    }
    const newPins = await this.createNewPins(pins);
    // sometimes during project change and first plan opening pins are not rendered on view
    // set timeout temp fix until better solution wont be finished
    await firstValueFrom(timer(0));
    this.tileViewerEvent.pinService.setPins(newPins);
    await this.handleFaultPins(plan);

  }

  private hasImageDimensions(plan: Plan): boolean {
    return !!plan.metaData?.ImageHeight && !!plan.metaData?.ImageWidth;
  }

  private async createNewPins(pins: FaultPin[]): Promise<Pin[]> {
    const newPins: Pin[] = [];
    for (const pin of pins) {
      const newPin = await this.convertToPin(pin);
      newPins.push(newPin);
    }
    return newPins;
  }

  private async setFaultTileVisiblity(currentFault: ViewFault, selectedPins: Pin[]) {
    const planEditMode = await firstValueFrom(this.planEditService.planEditMode$);
    const isFaultTileVisible = (selectedPins?.some(pin => (currentFault?.id != null && pin.data?.id === currentFault?.id) || (currentFault?.uid != null && pin?.id === currentFault?.uid)) ?? false) && planEditMode === PlanEditModeEnum.None;
    this.isFaultTileVisibleSubject.next(isFaultTileVisible);
    console.debug('isFaultTileVisible ### ' + isFaultTileVisible);
  }

  async mapSelectedFaultsOnPlan(): Promise<void> {
    const selectedFaults = await firstValueFrom(this.selectionFaultService.selectedItems.pipe(first()));
    selectedFaults.map(fault => this.tileViewerEvent.pinService.pins.find(p => p.id === fault.id))
      .filter(p => p)
      .forEach(p => this.tileViewerEvent.pinService.selectPin(p, false));

    const markedFault = await firstValueFrom(this.faultsViewService.currentFault.pipe(first()));
    const pin = this.tileViewerEvent.pinService.pins.find(p => p.id === markedFault?.id);
    if (pin) {
      this.tileViewerEvent.pinService.selectPin(pin, false);
    }
  }

  getPinMarkerOptions(attributeGroup: AttributeGroup, blobUrl?) {
    return {
      divIconOptions: {
        html: this.getAttributeImageHtml(blobUrl),
        iconSize: [24, 24],
        iconAnchor: [(24 * attributeGroup.RelativeAnchorToCanvasPositionX), (24 * attributeGroup.RelativeAnchorToCanvasPositionY)],
        popupAnchor: [0, 0],
        className: 'attribute-image'
      }
    } as PinMarkerOptions;
  }

  private setPins() {

  }

  private getAttributeImageHtml(blobUrl: string): string {
    if (!blobUrl) {
      return null;
    }
    return `<div style="width: 24px; height: 24px"><img style="width: 100%;height: 100%" src="${blobUrl}" class="mydocma-marker-img-view" alt="attribute Image"></div>`;
  }


  // todo ccreate map for chacing attribute url in attribute service
  async getAttributeGroup(attributeId: number) {
    let attributeGroup: AttributeGroup;
    if (attributeId) {
      const attribute = this.attributeService.getAttributeImage(attributeId);
      attributeGroup = this.attributeService.getAttributeGroup(attributeId);
      return {attributeGroup, blobUrl: attribute?.Url};
    }
  }

  private isPinTextValid(pinText: any): boolean {
    return pinText !== null && pinText !== undefined && pinText !== '';
  }

  private async createPin(pin: FaultPin, attributeGroup: AttributeGroup, blobUrl?) {
    const newPin: Pin = {
      id: pin.fault.identifier,
      anchor: {x: pin.coordinate.PosX / 100, y: pin.coordinate.PosY / 100},
      popupComponent: (!this.appContainerService.isMobileView ? this.faultsViewService.popupComponent : undefined),
      data: pin.fault,
      readonly: !this.editPinPermission,

    };
    if (pin.bpPin) {
      // MMW-3002: support dragged pin marker only for bp pin
      newPin.markerAnchor = (pin.coordinate?.RelativeAnchorToParentPosX && pin.coordinate?.RelativeAnchorToParentPosY ? {
        x: pin.coordinate.RelativeAnchorToParentPosX,
        y: pin.coordinate.RelativeAnchorToParentPosY
      } : undefined);
    }
    if (blobUrl) {
      newPin.data.attributeImage = blobUrl;
    }

    if (this.project.textPinEnabled && this.isPinTextValid(pin.fault?.pinText)) {

      newPin.markerOptions = {iconType: MarkerIconTypeEnum.textPin};
    } else if (blobUrl) {
      newPin.markerOptions = {iconType: MarkerIconTypeEnum.attributeImage};
    } else if (pin.bpPin?.Locations?.find(l => l.Type === BpPinLocationTargetEnum.bim)) {
      newPin.markerOptions = {iconType: MarkerIconTypeEnum.defaultWithObject};
    }

    return newPin;
  }

  public async convertToPin(faultPin: FaultPin): Promise<Pin> {
    const fault = faultPin.fault;
    const attributeGroupParam = await this.getAttributeGroup(faultPin.fault.data?.Attribute?.Id);
    return this.createPin(faultPin, attributeGroupParam?.attributeGroup, attributeGroupParam?.blobUrl);
  }


}
